import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Shield,
  Key,
  Clock,
  AlertCircle,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';

// Company Profile
export const CompanyProfile = () => {
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    toast.success('Company profile updated!');
    setIsEditing(false);
  };

  return (
    <div className="space-y-6" data-testid="company-profile">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            Company Profile
          </h1>
          <p className="text-muted-foreground mt-1">Manage your organization details</p>
        </div>
        <Button
          onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
          className={isEditing ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          {isEditing ? (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          ) : (
            'Edit Profile'
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <Card className="border border-border lg:col-span-2">
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
            <CardDescription>Basic organization details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input defaultValue="FinMatrixRQ Pvt Ltd" disabled={!isEditing} />
              </div>
              <div className="space-y-2">
                <Label>Registration Number</Label>
                <Input defaultValue="U74999MH2020PTC123456" disabled={!isEditing} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input defaultValue="info@FinMatrixRQ.com" disabled={!isEditing} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input defaultValue="+91 22 1234 5678" disabled={!isEditing} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input defaultValue="https://www.FinMatrixRQ.com" disabled={!isEditing} />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea
                defaultValue="123 Finance Street, Andheri East, Mumbai, Maharashtra 400069, India"
                disabled={!isEditing}
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Info */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Quick Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/50">
              <div className="p-2 rounded-lg bg-background">
                <Building2 className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Active Since</p>
                <p className="text-xs text-muted-foreground">March 2020</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/50">
              <div className="p-2 rounded-lg bg-background">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Support Email</p>
                <p className="text-xs text-muted-foreground">support@FinMatrixRQ.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/50">
              <div className="p-2 rounded-lg bg-background">
                <Phone className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-medium">Helpline</p>
                <p className="text-xs text-muted-foreground">1800-XXX-XXXX</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Role Permissions
export const RolePermissions = () => {
  const permissions = [
    { module: 'Dashboard', admin: true, employee: true, agent: true },
    { module: 'Leads Management', admin: true, employee: true, agent: true },
    { module: 'Add/Edit Leads', admin: true, employee: true, agent: true },
    { module: 'Delete Leads', admin: true, employee: false, agent: false },
    { module: 'Loan Processing', admin: true, employee: true, agent: false },
    { module: 'Approve Loans', admin: true, employee: false, agent: false },
    { module: 'Agent Management', admin: true, employee: false, agent: false },
    { module: 'Employee Management', admin: true, employee: false, agent: false },
    { module: 'Commission Management', admin: true, employee: false, agent: false },
    { module: 'View Commission', admin: true, employee: true, agent: true },
    { module: 'Reports', admin: true, employee: true, agent: false },
    { module: 'System Settings', admin: true, employee: false, agent: false },
  ];

  return (
    <div className="space-y-6" data-testid="role-permissions">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            Role & Permissions
          </h1>
          <p className="text-muted-foreground mt-1">Configure access permissions by role</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Module / Permission</TableHead>
                <TableHead className="text-center">Admin</TableHead>
                <TableHead className="text-center">Employee</TableHead>
                <TableHead className="text-center">Agent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map((perm) => (
                <TableRow key={perm.module}>
                  <TableCell className="font-medium">{perm.module}</TableCell>
                  <TableCell className="text-center">
                    <Checkbox checked={perm.admin} disabled />
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox checked={perm.employee} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox checked={perm.agent} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Audit Logs
export const AuditLogs = () => {
  const logs = [
    { id: 1, user: 'Rajesh Kumar (Admin)', action: 'Updated commission slab CS002', ip: '192.168.1.100', timestamp: '2025-01-24 14:30:00' },
    { id: 2, user: 'Priya Sharma (Employee)', action: 'Approved loan LN003', ip: '192.168.1.105', timestamp: '2025-01-24 12:15:00' },
    { id: 3, user: 'Amit Patel (Agent)', action: 'Added new lead L011', ip: '103.45.67.89', timestamp: '2025-01-24 10:45:00' },
    { id: 4, user: 'Rajesh Kumar (Admin)', action: 'Created new employee', ip: '192.168.1.100', timestamp: '2025-01-23 16:20:00' },
    { id: 5, user: 'System', action: 'Daily backup completed', ip: 'localhost', timestamp: '2025-01-23 03:00:00' },
    { id: 6, user: 'Karthik Nair (Employee)', action: 'Updated lead status L008', ip: '192.168.1.108', timestamp: '2025-01-22 15:30:00' },
  ];

  return (
    <div className="space-y-6" data-testid="audit-logs">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Audit Logs</h1>
        <p className="text-muted-foreground mt-1">System activity and security logs</p>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-medium">{log.user}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell className="font-mono text-sm">{log.ip}</TableCell>
                  <TableCell className="text-muted-foreground">{log.timestamp}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Security Settings
export const SecuritySettings = () => {
  return (
    <div className="space-y-6" data-testid="security-settings">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
          Security Settings
        </h1>
        <p className="text-muted-foreground mt-1">Configure security and access policies</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Password Policy */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Password Policy
            </CardTitle>
            <CardDescription>Configure password requirements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Minimum Length</p>
                <p className="text-sm text-muted-foreground">At least 8 characters</p>
              </div>
              <Input className="w-20" type="number" defaultValue="8" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Require Uppercase</p>
                <p className="text-sm text-muted-foreground">At least one capital letter</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Require Numbers</p>
                <p className="text-sm text-muted-foreground">At least one number</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Require Special Characters</p>
                <p className="text-sm text-muted-foreground">At least one special character</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Session Settings */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Session Settings
            </CardTitle>
            <CardDescription>Configure session timeout and behavior</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Session Timeout</p>
                <p className="text-sm text-muted-foreground">Auto logout after inactivity</p>
              </div>
              <div className="flex items-center gap-2">
                <Input className="w-20" type="number" defaultValue="30" />
                <span className="text-sm text-muted-foreground">mins</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Remember Me</p>
                <p className="text-sm text-muted-foreground">Allow persistent sessions</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Single Session</p>
                <p className="text-sm text-muted-foreground">Limit to one active session</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>Additional security layer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable 2FA</p>
                <p className="text-sm text-muted-foreground">Require for all users</p>
              </div>
              <Switch />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS OTP</p>
                <p className="text-sm text-muted-foreground">Send OTP via SMS</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Email OTP</p>
                <p className="text-sm text-muted-foreground">Send OTP via Email</p>
              </div>
              <Switch defaultChecked />
            </div>
          </CardContent>
        </Card>

        {/* Login Alerts */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Login Alerts
            </CardTitle>
            <CardDescription>Get notified of suspicious activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">New Device Alert</p>
                <p className="text-sm text-muted-foreground">Alert on new device login</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Failed Login Alert</p>
                <p className="text-sm text-muted-foreground">Alert after 3 failed attempts</p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Location Change</p>
                <p className="text-sm text-muted-foreground">Alert on unusual location</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button className="bg-emerald-600 hover:bg-emerald-700">
          <Save className="h-4 w-4 mr-2" />
          Save Security Settings
        </Button>
      </div>
    </div>
  );
};

export default CompanyProfile;
